import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@core/i18n';
import { useBookingFlow } from '@features/booking/flow';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useAuth } from '@core/auth';
import {
  useAnalytics,
  deriveSourceStepFromPath,
  categorizeCommitError,
  safeErrorCode,
  makeCommitAttemptEvent,
  makeCommitFailureEvent,
} from '@core/analytics';
import type { CommitFunnelAttribution } from '@core/analytics';
import { commitErrorKey } from '@features/booking/utils/commitMomentCopy';
import { ThreeDsError } from '@features/booking/hooks/handle3dsChallenge';
import { timeSlotsKeys } from '@features/booking/hooks/useTimeSlots';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { commitReservation } from '@features/booking/services/commitReservation';
import { CommitError } from '@features/booking/services/commit';

/**
 * Result shape returned to the page. The hook owns React state for the
 * commit lifecycle; the page reads `commit`, renders the button against
 * `isSubmitting`, displays `error`, and shows the reload prompt when
 * `requireReload` is true.
 */
export interface UseReservationCommitResult {
  /** Invoke once per user click. Idempotent against double-fire only via React's setIsSubmitting button-disable. */
  commit: () => Promise<void>;
  isSubmitting: boolean;
  error: string | null;
  /** MP-only: true after Stripe.js v3 loaded during 3DS. Page must show reload-to-retry UX. */
  requireReload: boolean;
  /**
   * True while a 3DS challenge is active (between path-module on3dsStart and
   * on3dsEnd). Pages render <ThreeDSChallengeOverlay open={isHandling3ds} />
   * against this flag — see T30 / D2 in the plan.
   */
  isHandling3ds: boolean;
}

/**
 * React hook that wraps commitReservation. Pulls all the providers/context it
 * needs internally so the calling page just receives `{ commit, isSubmitting,
 * error, requireReload }`.
 *
 * The hook is "thin" — it owns React state (the three useState calls) and
 * error-to-UI mapping. The reservation logic itself lives in the framework-
 * free commitReservation function.
 *
 * isSubmitting deferral: the original handleConfirm only flipped
 * isSubmitting AFTER guards passed. The hook mirrors this by passing an
 * onCommitStart callback to commitReservation; it fires after guards pass
 * but before the first await. So guard rejections never flicker the button.
 *
 * Telemetry (T26): fires BOOKING_COMMIT_ATTEMPT on every click before guards
 * run, and BOOKING_COMMIT_FAILURE in the catch block. Both carry a
 * `source_step` attribute derived from the URL pathname so the same metric
 * works pre- and post-flag-flip (confirm | guarantee | personalize).
 */
export function useReservationCommit(): UseReservationCommitResult {
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const { dateLocale, currentLanguage } = useLanguage();
  const { goToNext, goToStep } = useBookingFlow();
  const booking = useBooking();
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { trackConversion, trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const { feeBreakdown } = usePaymentFee();
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // MP-only: set after a ThreeDsError. Stripe.js v3 loads lazily inside
  // handle3dsChallenge and overwrites window.Stripe, killing the v2 instance
  // the MP form used to tokenize. Retry requires a fresh page load.
  const [requireReload, setRequireReload] = useState(false);
  // T30: flipped true by the commit path module when handle3dsChallenge is
  // about to fire; flipped back in the path's finally. The page renders
  // <ThreeDSChallengeOverlay open={isHandling3ds} /> against this.
  const [isHandling3ds, setIsHandling3ds] = useState(false);

  // T26 telemetry: track when the hook first mounted on this step so we can
  // compute time_on_step_ms (mount → commit click). useRef ensures we only
  // capture the first-mount timestamp, not every re-render.
  const mountedAtRef = useRef<number>(Date.now());

  const invalidateTimeSlots = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: timeSlotsKeys.all });
  }, [queryClient]);

  const commit = useCallback(async () => {
    setError(null);

    // Build the funnel-event attribution that BOTH commit_attempt and
    // commit_failure events share. Computed once per click so the timing is
    // pinned at the moment the user pressed the button.
    const attribution: CommitFunnelAttribution = {
      source_step: deriveSourceStepFromPath(location.pathname),
      guarantee_type: booking.isEarlyAccessFlow ? 'early_access' : booking.guaranteeType,
      payment_processor: booking.paymentProcessor ?? 'none',
      is_waiting_list: booking.flowType === 'waiting_list',
      time_on_step_ms: Date.now() - mountedAtRef.current,
    };

    // Fire the attempt event BEFORE the API call. Guards in commitReservation
    // may short-circuit (return 'guarded') — but the user did click, so the
    // attempt should land regardless. The dashboard separates "clicked, didn't
    // succeed" from "clicked, succeeded" via the success event firing or not.
    //
    // Local try/catch protects against a broken trackEvent contract (e.g., a
    // partially-mocked provider, a content-blocker-shim that throws). The
    // AnalyticsProvider already hardens window.gtag calls with safeCall, but
    // the hook's invariant — "the user sees the booking outcome regardless of
    // analytics state" — is too important to depend on the provider alone.
    try {
      trackEvent(makeCommitAttemptEvent(attribution));
    } catch {
      // Analytics is observational. Never break the booking on its account.
    }

    try {
      await commitReservation(
        {
          booking,
          restaurant,
          authState,
          feeBreakdown,
          currentLanguage,
          dateLocale,
          trackConversion,
          invalidateTimeSlots,
          goToNext,
          goToStep,
          // T30 overlay: piped down so depositStripe/depositMercadoPago can
          // flip the UI flag around their handle3dsChallenge call.
          on3dsStart: () => setIsHandling3ds(true),
          on3dsEnd: () => setIsHandling3ds(false),
        },
        // Fires after guards pass, before any await. This is when the button
        // should disable. Guard rejections don't call this, so no flicker.
        () => setIsSubmitting(true),
      );
    } catch (err) {
      const cause = err instanceof CommitError ? err.cause : err;
      const mpFlow3dsLoaded = err instanceof CommitError ? err.mpFlow3dsLoaded : false;

      // MP-only reload guard: if this submit loaded Stripe.js v3 (via
      // handle3dsChallenge with requires_action=true), v2 is dead globally
      // and any retry without page reload would silently produce a bad
      // token. Set the reload UX regardless of error type. Stripe-primary
      // ThreeDsErrors don't trigger this — v3 was already loaded by
      // Elements before submit, no v2 to break.
      if (mpFlow3dsLoaded) {
        setRequireReload(true);
      }

      // T26 telemetry: fire the failure event with a coarse category. Sentry
      // already captures full error context via capturePaymentError inside
      // commitReservation; this event is for funnel monitoring only.
      // Wrapped in try/catch so a broken trackEvent never suppresses the
      // setError call below — the user-visible error message is the contract.
      const causeAsError = cause instanceof Error ? cause : null;
      try {
        trackEvent(
          makeCommitFailureEvent(attribution, {
            error_category: categorizeCommitError({
              causeName: causeAsError?.name,
              causeMessage: causeAsError?.message ?? '',
            }),
            error_code: safeErrorCode(cause),
          }),
        );
      } catch {
        // Analytics is observational. Never break the booking on its account.
      }

      // Error → user-facing copy. Three precedence rules:
      //
      // 1. ThreeDsError → cause.code as the i18n key (errors namespace).
      //    The codes are discrete and CRITICAL to keep distinct:
      //    - PAYMENT_3DS_CANCELLED  → user cancelled the bank challenge
      //    - PAYMENT_3DS_FAILED     → bank refused verification
      //    - PAYMENT_3DS_CONFIRM_ERROR → CARD WAS CHARGED but confirm API
      //      failed — user must NOT retry (would double-charge) and should
      //      contact the restaurant. Collapsing this to a generic "try
      //      again" message would invite the double-charge scenario.
      //    See handle3dsChallenge.ts for the ThreeDsErrorCode union.
      //
      // 2. Backend codes (RESTAURANT_INACTIVE, BLOCKED_USER, MONTHLY_LIMIT)
      //    keep their existing per-code messages — most precise copy.
      //
      // 3. Anything else falls through to the category-based commit error
      //    key (T30 D4): 3DS rejected (non-ThreeDsError 3DS errors), card
      //    declined, network, generic.
      if (cause instanceof ThreeDsError) {
        setError(tErrors(cause.code));
      } else {
        const rawMessage = cause instanceof Error ? cause.message : '';
        const backendErrorMessages: Record<string, string> = {
          RESTAURANT_INACTIVE: tErrors('GENERAL_SERVER_ERROR'),
          BLOCKED_USER_SUBTITLE: t('BLOCKED_USER_SUBTITLE'),
          MONTHLY_LIMIT_REACHED: t('CONFIRMATION_MONTHLY_LIMIT'),
        };
        const backendCopy = backendErrorMessages[rawMessage];
        if (backendCopy) {
          setError(backendCopy);
        } else {
          const category = categorizeCommitError({
            causeName: cause instanceof Error ? cause.name : undefined,
            causeMessage: rawMessage,
          });
          setError(tErrors(commitErrorKey(category)));
        }
      }
    } finally {
      setIsSubmitting(false);
      // Belt-and-suspenders: even if a path module forgot to call on3dsEnd
      // (e.g., a future refactor or a thrown error before the finally), force
      // the overlay closed at the end of every commit. Without this, a stuck
      // overlay could trap the user behind an invisible modal.
      setIsHandling3ds(false);
    }
  }, [
    booking,
    restaurant,
    authState,
    feeBreakdown,
    currentLanguage,
    dateLocale,
    trackConversion,
    trackEvent,
    invalidateTimeSlots,
    goToNext,
    goToStep,
    location.pathname,
    t,
    tErrors,
  ]);

  return { commit, isSubmitting, error, requireReload, isHandling3ds };
}
